import { HashLink } from 'react-router-hash-link';
import type { JSX } from 'react';

function AboutMetricsGtap(): JSX.Element {
  return (
    <div id="gtap">
      <h3>Gene Therapeutic Area Profile (GTAP)</h3>
      <p>
        Every gene in DISGENET is linked to one or more diseases — but a raw list of associations doesn&apos;t
        tell you what kind of gene you&apos;re looking at. Is it a specialist, concentrated in one corner of
        human disease? Or a generalist, showing up everywhere from cancer to metabolic disorders?
      </p>
      <p>
        That&apos;s the question the Gene Therapeutic Area Profile (GTAP) answers. For every gene, GTAP
        compares how its associated diseases are distributed across major disease classes against the DISGENET
        background as a whole, surfacing which classes a gene is enriched in and which it&apos;s conspicuously
        absent from.
      </p>
      <p>
        <strong>PCSK9</strong>, for example, comes back concentrated in Nutritional and Metabolic Diseases and
        Cardiovascular Diseases, exactly where you&apos;d expect a cholesterol-regulating gene to land. While{' '}
        <strong>PSEN1</strong> lights up almost entirely in Nervous System and Behavior and Mental Disorders —
        with Mental Disorders, Behavior and Behavior Mechanisms, and Nervous System all reaching significance
        — a profile that fits its role in Alzheimer&apos;s disease and is depleted in many other classes
        (Immune System, Hemic and Lymphatic, Neoplasms, Digestive System all significantly negative).{' '}
        <strong>IL15</strong>, by contrast, shows the signature of an immune-signaling gene: significant
        enrichment in Infections, Immune System, and Hemic and Lymphatic diseases, alongside significant
        depletion in Congenital &amp; Hereditary and Endocrine disease classes — a tight, immunology-specific
        footprint rather than a broad one.
      </p>
      <p>
        GTAP profiles are available for genes across the platform. They complement existing metrics like the{' '}
        <HashLink smooth to="/docs?section=metrics&subsection=specificity" className="js-fix-scroll">
          Disease Specificity Index (DSI)
        </HashLink>{' '}
        and the{' '}
        <HashLink smooth to="/docs?section=metrics&subsection=pleiotropy" className="js-fix-scroll">
          Disease Pleiotropy Index (DPI)
        </HashLink>
        .
      </p>
      <p className="text-sm text-gray-500 mt-1">
        For each MeSH therapeutic class, a Therapeutic Class Enrichment Score (TCES) is calculated as the log₂
        ratio between the observed frequency of the gene&apos;s disease associations in that class and its
        expected frequency in DISGENET. Positive TCES values indicate enrichment, zero indicates the expected
        representation, and negative values indicate underrepresentation. The complete set of TCES values
        across therapeutic classes constitutes the GTAP.
      </p>
    </div>
  );
}

export default AboutMetricsGtap;
